/**
 * Talker Q&A landing pages (Sam, Oct 21 2026). Written for AI/search answers.
 * Copy rules: conversation, never clinical wording; no ages; "they"; emotional benefit first.
 */
const FREE = 'Your first 15 minutes are free.';

const base = [
  {
    question: 'What is this site?',
    answer:
      'This is a place to talk one-on-one with a friendly, ID-verified listener for 15 minutes. You pick someone who shares your interests, and they give you their full attention while you unload, share good news or just chat.',
  },
  {
    question: 'How much does it cost?',
    answer: `Each listener sets their own prices: text can be free, voice starts at $5 and video at $10. ${FREE} You pay only for the time you book, with no subscription and no commitment.`,
  },
  {
    question: 'Can I talk by text, voice or video?',
    answer:
      'Yes. Choose text chat if you want to type quietly, voice if you want to say it out loud, or video if you want it to feel face to face. You pick the format before the session starts.',
  },
  {
    question: 'Can I talk right now or book a time?',
    answer:
      'Both. Listeners who show "Available now" can start a session with you right away. You can also book a time that suits you from their open times.',
  },
];
const privateQ = {
  question: 'Is it private?',
  answer:
    'Yes. Sessions are one-on-one and online, and listeners see only your display name. A photo is optional. This is a friendly conversation, not a crisis service; in an emergency, call or text 988 in the US.',
};
const faqs = talkQ => [...base, talkQ, privateQ];

export const TALKER_QA_PAGES = [
  {
    id: 'someone-to-talk-to',
    path: '/someone-to-talk-to',
    name: 'SomeoneToTalkToPage',
    title: 'Need someone to talk to? Your first 15 minutes are free | Sweet Listeners',
    description:
      'Feel heard and leave lighter. Talk one-on-one with a kind, verified listener by text, voice or video. Sessions from $5. Your first 15 minutes are free.',
    h1: 'Where can I find someone to talk to?',
    answer: [
      'Here you can talk with a kind, ID-verified listener for 15 minutes, any time you need to feel heard. Pick someone who shares your interests and chat by text, voice or video, right now or at a time you book.',
      FREE,
    ],
    talk: 'Anything on your mind: a rough day, good news, family, work, a show you love or your team’s last game. You lead the conversation, and they listen.',
    topics: [['everyday-life', 'Everyday life'], ['venting', 'Venting'], ['relationships', 'Relationships'], ['sports', 'Sports'], ['tv-and-movies', 'TV and movies'], ['good-news', 'Good news']],
  },
  {
    id: 'sports',
    path: '/talk-to-someone-about-sports',
    name: 'TalkToSomeoneAboutSportsPage',
    title: 'Talk to someone about sports. Your first 15 minutes are free | Sweet Listeners',
    description:
      'Share the thrill of the game with a fellow fan. Talk football, baseball, basketball or hockey one-on-one by text, voice or video. Your first 15 minutes are free.',
    h1: 'Is there someone I can talk sports with?',
    answer: [
      'Yes. We have listeners who love football, baseball, basketball and hockey, and they’re happy to relive the big play, argue the trade or get hyped for the next game with you. It’s the fun of talking with a fellow fan, whenever your friends aren’t around.',
      FREE,
    ],
    talk: 'Last night’s game, your team’s season, fantasy picks, trades, rivalries or the play everyone is still talking about. Search by sport or team to find a listener who follows yours.',
    topics: [['sports', 'Sports'], ['gaming', 'Gaming'], ['everyday-life', 'Everyday life']],
  },
  {
    id: 'at-night',
    path: '/someone-to-talk-to-at-night',
    name: 'SomeoneToTalkToAtNightPage',
    title: 'Someone to talk to at night. Your first 15 minutes are free | Sweet Listeners',
    description:
      'Can’t sleep and your mind won’t quiet down? Talk with a kind, verified listener by text, voice or video. Sessions from $5. Your first 15 minutes are free.',
    h1: 'Who can I talk to late at night?',
    answer: [
      'When the house is quiet and your thoughts are loud, a short chat can help you settle and feel calmer. We show which listeners are available now, and text chat lets you talk without waking anyone.',
      FREE,
    ],
    talk: 'Whatever is keeping you up: a busy mind, something that happened today, tomorrow’s plans, or just wanting some friendly company after a night shift. Availability depends on who is online, so you can also book a time.',
    topics: [['venting', 'Venting'], ['everyday-life', 'Everyday life'], ['binge-worthy-shows', 'Binge-worthy shows']],
  },
  {
    id: 'kind-online',
    path: '/talk-to-someone-kind-online',
    name: 'TalkToSomeoneKindOnlinePage',
    title: 'Talk to someone kind online. Your first 15 minutes are free | Sweet Listeners',
    description:
      'Feel cared for in just 15 minutes. Chat one-on-one with a warm, verified listener who gives you their full attention. Your first 15 minutes are free.',
    h1: 'Where can I talk to someone kind online?',
    answer: [
      'We’re built around kindness: every listener is ID-verified and chose to be here because they enjoy giving people their full attention. You get a warm, patient conversation partner for 15 minutes, by text, voice or video.',
      FREE,
    ],
    talk: 'Anything that would feel better shared: your day, a worry, a win, a memory, a hobby. Read listener profiles to find someone whose interests and personality feel right.',
    topics: [['everyday-life', 'Everyday life'], ['good-news', 'Good news'], ['friendship', 'Friendship'], ['pets', 'Pets']],
  },
  {
    id: 'tv-music',
    path: '/chat-about-tv-shows-and-music',
    name: 'ChatAboutTvShowsAndMusicPage',
    title: 'Chat about TV shows and music. Your first 15 minutes are free | Sweet Listeners',
    description:
      'Just finished a great show or album? Share the excitement with a fan who gets it. Chat by text, voice or video. Your first 15 minutes are free.',
    h1: 'Who can I chat with about TV shows and music?',
    answer: [
      'Finishing a great series or album is more fun when you can talk about it. We match you with listeners who love the same shows, movies, music and books, so you can gush, theorize and swap recommendations for 15 minutes.',
      FREE,
    ],
    talk: 'That finale, your favorite characters, fan theories, the album on repeat, concerts, books and what to watch next.',
    topics: [['tv-and-movies', 'TV and movies'], ['binge-worthy-shows', 'Binge-worthy shows'], ['music', 'Music'], ['books', 'Books'], ['true-crime', 'True crime']],
  },
  {
    id: 'without-judgment',
    path: '/someone-to-listen-without-judgment',
    name: 'SomeoneToListenWithoutJudgmentPage',
    title: 'Someone to listen without judgment. Your first 15 minutes are free | Sweet Listeners',
    description:
      'Say it all and feel lighter. A kind, verified listener hears you out without judging, interrupting or lecturing. Text, voice or video. Your first 15 minutes are free.',
    h1: 'Where can I find someone to listen without judgment?',
    answer: [
      'Sometimes you just need to say it all to someone who won’t judge, interrupt or lecture you. A listener gives you 15 minutes of calm, full attention, and they don’t know anyone in your life, so you can be completely honest.',
      FREE,
    ],
    talk: 'Whatever you’ve been holding in: stress, family or relationship frustrations, work, a decision you’re stuck on, or something you’ve never said out loud. Tell them if you just want to vent or would like a question or two.',
    topics: [['venting', 'Venting'], ['relationships', 'Relationships'], ['family', 'Family'], ['work', 'Work']],
  },
].map(p => ({ ...p, faqs: faqs({ question: 'What can I talk about?', answer: p.talk }) }));

export const talkerQaPageById = id => TALKER_QA_PAGES.find(p => p.id === id) || null;
